import { useRef, useEffect, useState, useCallback } from 'react';

interface Position {
  x: number;
  y: number;
}

export const useDraggable = (handleSelector: string) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Limitar o movimento para não sair da tela
    const element = elementRef.current;
    if (!element) return;
    
    const maxX = window.innerWidth - element.offsetWidth;
    const maxY = window.innerHeight - element.offsetHeight;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Centralizar o modal na primeira renderização
    if (!isInitialized) {
      const centerX = (window.innerWidth - element.offsetWidth) / 2;
      const centerY = (window.innerHeight - element.offsetHeight) / 2;
      setPosition({ x: centerX, y: centerY });
      setIsInitialized(true);
    }

    const handleElement = element.querySelector(handleSelector) as HTMLElement;
    if (!handleElement) return;

    const handleMouseDown = (e: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
      e.preventDefault();
      e.stopPropagation();
    };

    handleElement.addEventListener('mousedown', handleMouseDown);

    return () => {
      handleElement.removeEventListener('mousedown', handleMouseDown);
    };
  }, [handleSelector, isInitialized]);

  const resetPosition = useCallback(() => {
    setPosition({ x: 0, y: 0 });
    setIsInitialized(false);
    setIsDragging(false);
  }, []);

  return {
    elementRef,
    position,
    isDragging,
    resetPosition,
    style: {
      transform: `translate(${position.x}px, ${position.y}px)`,
      cursor: isDragging ? 'grabbing' : 'default',
    },
  };
};